import {
  FormControl,
  FormErrorMessage,
  SearchSelect,
  usePrevious,
} from '@wegroup/design-system';
import { isEmpty } from 'lodash';
import React, { useLayoutEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormOption } from '../../../../../../../../types/Form';
import useAnswerForQuestion from '../../../../../hooks/useAnswerForQuestion';
import useFlowInputComponent from '../../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../../types/FlowsTypes';
import useEnumOptions from '../hooks/useEnumOptions';
import useTargetQuestion from '../hooks/useTargetQuestion';

interface Props {
  question: FlowsQuestion;
}

const IndexSelectInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const {
    field: { value, onBlur },
    error,
    prefilled,
  } = useFlowInputComponent({ question });
  const getAnswerForQuestion = useAnswerForQuestion();

  const chosenValue = (value != null ? parseInt(value) : undefined) as
    | number
    | undefined;

  const enumOptions: FormOption[] = useEnumOptions(question);

  const targetQuestion = useTargetQuestion(question);
  const targetQuestionAnswer = getAnswerForQuestion(targetQuestion!.id);
  // TODO: Make useFlowsAnswers hook
  const { setValue } = useFormContext();
  const previousEnumOptions = usePrevious(enumOptions);

  /**
   * We want to make sure that the answer is cleared if it is no longer an answer for the targetQuestion
   */
  useLayoutEffect(() => {
    // Answer was not removed, update indices
    const currentOption = previousEnumOptions?.find(
      ({ value }) => parseInt(value) === chosenValue,
    );
    const newIndex =
      enumOptions.find(({ label }) => label === currentOption?.label)?.value ||
      null;

    onBlur({ name: question.id, value: newIndex });
    setValue(question.id, newIndex);
  }, [targetQuestionAnswer]);

  const handleOnChange = (value: unknown) => {
    if (typeof value === 'object' && isEmpty(value))
      onBlur({ name: question.id, value: '' });
    else onBlur({ name: question.id, value: value as string });
  };

  return (
    <FormControl isInvalid={!!error}>
      <SearchSelect
        clearable
        placeholder={t('DEFAULT_SELECT_PLACEHOLDER')}
        onChange={handleOnChange}
        data={enumOptions}
        error={error?.message}
        value={chosenValue?.toString() || null}
        prefilled={prefilled}
        data-test-id={`FLOWS_SELECT-INPUT_${question.id
          .toUpperCase()
          .replaceAll('.', '-')}`}
        // Legacy styling to match flows inputs
        extraStyles={{
          input: {
            height: '45px',
            boxShadow:
              '0 4px 8px rgba(0,0,0,0.04),0 0 2px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)',
            borderColor: prefilled ? '#FFBC0F' : 'rgb(228, 228, 228)',
          },
          dropdown: {
            borderColor: 'rgb(228, 228, 228)',
          },
        }}
        isDisabled={question.rules.is_disabled}
      />
      <FormErrorMessage justifyContent="flex-end">
        {error?.message}
      </FormErrorMessage>
    </FormControl>
  );
};

export default IndexSelectInputComponent;
